import classnames from 'classnames';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import { MultiSelect } from 'primereact/multiselect';
import PropTypes from 'prop-types';
import React from 'react';
import { SegmentEventTypes } from '../../data/tracking';
import Analytics from '../../utils/Analytics';
import LabelWrapper from '../LabelWrapper.component';

/**
 * Custom item template.
 * @param {object} option
 * @return { ReactElement }
 */
const itemTemplate = option => {
	const { name = '', tagColor, dataqa } = option;
	const cls = 'multiselect-item-template';

	return (
		<div className={cls} {...{ dataqa }}>
			{/* Optionally rendering Tag color bar if `tagColor` key is present */}
			{isString(tagColor) ? (
				<span className="tag-color" style={{ background: tagColor }} />
			) : null}
			<span className="name">{name}</span>
		</div>
	);
};

/**
 * Custom selected item template.
 * @param {string | object } option When `optionValue` prop is passed to MultiSelect, or
 * when 'value' key is present in options array, it will be string, otherwise, it's an object
 * @return { ReactElement }
 */
const selectedItemTemplate = option => {
	const cls = 'multiselect-selected-item-template';

	if (isObject(option)) {
		return (
			<div className={cls} {...{ dataqa: option.dataqa }}>
				{/* Optionally rendering Tag color bar if `tagColor` key is present */}
				{isString(option.tagColor) ? (
					<span className="tag-color" style={{ background: option.tagColor }} />
				) : null}
				<span className="name">{option.name}</span>
			</div>
		);
	} else if (isString(option)) {
		return (
			<span className={cls} {...{ dataqa: option.dataqa }}>
				{option}
			</span>
		);
	}

	// needs to return null otherwise placeholder will not be rendered.
	return null;
};

export const WrappedMultiSelect = props => {
	const {
		className,
		isInputInvalid,
		dataqa = 'common',
		onFocus,
		onBlur,
		...rest
	} = props;

	const focusHandler = e => {
		Analytics.trackSegmentEvent({
			trackingData: {
				field_type: 'multiselect',
				field_name_dataqa: `ui-multiselect-${dataqa}`,
			},
			eventType: SegmentEventTypes.UserInputStarted,
		});

		if (isFunction(onFocus)) {
			onFocus(e);
		}
	};

	const blurHandler = e => {
		Analytics.trackSegmentInputEvent({
			trackingData: {
				field_type: 'multiselect',
				field_name_dataqa: `ui-multiselect-${dataqa}`,
			},
			eventType: SegmentEventTypes.UserInputCompleted,
			fieldValue: e.target?.value,
		});

		if (isFunction(onBlur)) {
			onBlur(e);
		}
	};

	const multiSelectClassName = classnames(className, {
		'invalid-component': isInputInvalid,
	});

	return (
		<div
			className="common-ui-mulitiselect"
			data-qa={`ui-multiselect-${dataqa}`}
		>
			<MultiSelect
				className={multiSelectClassName}
				itemTemplate={itemTemplate}
				selectedItemTemplate={selectedItemTemplate}
				appendTo="self"
				dataqa={`ui-multiselect-${dataqa}`}
				onFocus={focusHandler}
				onBlur={blurHandler}
				{...rest}
			/>
		</div>
	);
};

export const WrappedMultiSelectWithLabel = LabelWrapper(WrappedMultiSelect);

WrappedMultiSelect.propTypes = {
	// style class of the element
	className: PropTypes.string,

	dataqa: PropTypes.string,

	// function that gets the option and returns the content for it
	itemTemplate: PropTypes.func,

	// function that gets an item in the value and returns the content for it
	selectedItemTemplate: PropTypes.func,
};
